import React, {
  ComponentPropsWithoutRef,
  CSSProperties,
  forwardRef,
  Ref,
  RefObject,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { FieldColorNames } from 'Hooks/DocumentFields/useDocumentFieldColor';
import { InteractFieldModalPosition } from 'Interfaces/Document';
import { DocumentFieldTypes } from 'Interfaces/DocumentFields';

import FieldItemDefaultTrigger from './FieldItemDefaultTrigger';

interface FieldItemViewProps extends ComponentPropsWithoutRef<'div'> {
  style: CSSProperties;
  fieldType: DocumentFieldTypes;
  renderFieldTrigger?: () => JSX.Element | undefined;
  renderFieldMenu?: () => JSX.Element | undefined;
  id?: string;
  className?: string;
  dropdownClassName?: string;
  dropdownMenuRef?: RefObject<HTMLDivElement>;
  fieldColor?: FieldColorNames;
  isMenuOpen?: boolean;
  isResizable?: boolean;
  inFocus?: boolean;
  isFilled?: boolean;
  selectedScale: number;
  fieldModalPosition?: InteractFieldModalPosition;
}

const FieldItemView = (
  {
    id,
    fieldType,
    style,
    fieldColor = FieldColorNames.EMPTY,
    inFocus,
    renderFieldTrigger,
    renderFieldMenu,
    className,
    dropdownClassName,
    dropdownMenuRef,
    isMenuOpen = false,
    isResizable = false,
    isFilled = false,
    selectedScale,
    fieldModalPosition = InteractFieldModalPosition.TOP,
    ...restProps
  }: FieldItemViewProps,
  ref: Ref<HTMLDivElement>,
) => {
  const dropDownMenuStyles = useMemo(() => {
    const translationCoefficient = ((1 - selectedScale) * 100) / 2;

    let x = 0;
    let y = 0;

    switch (fieldModalPosition) {
      case InteractFieldModalPosition.TOP:
        x = translationCoefficient;
        y = -translationCoefficient;
        break;
      case InteractFieldModalPosition.BOTTOM:
        x = translationCoefficient;
        y = translationCoefficient;
        break;
      case InteractFieldModalPosition.LEFT:
        x = -translationCoefficient;
        y = translationCoefficient;
        break;
      case InteractFieldModalPosition.TOP_LEFT:
        x = -translationCoefficient;
        y = -translationCoefficient;
        break;
    }

    return {
      transform: `scale(${1 / selectedScale}) translate(${x}%, ${y}%)`,
    };
  }, [fieldModalPosition, selectedScale]);

  return (
    <div
      ref={ref}
      tabIndex={1}
      className={classNames('fieldDropDown', 'draggable', className, {
        'fieldDropDown--opened': isMenuOpen,
      })}
      role="presentation"
      data-field-type={fieldType}
      data-coord-x={style.left}
      data-coord-y={style.top}
      data-size-width={style.width}
      data-size-height={style.height}
      id={id}
      style={style}
      {...restProps}
    >
      <div
        className={classNames(
          'fieldDropDown__trigger',
          `fieldDropDown__trigger--${fieldType}`,
          `fieldColor__${fieldColor ?? FieldColorNames.EMPTY}`,
          {
            'fieldDropDown__trigger--inFocus': inFocus,
            'fieldDropDown__trigger--filled': isFilled,
          },
        )}
      >
        {renderFieldTrigger ? (
          renderFieldTrigger()
        ) : (
          <FieldItemDefaultTrigger fieldType={fieldType} />
        )}
        {isResizable && (
          <>
            <div className="fieldDropDown__trigger-triangle fieldDropDown__trigger-triangle--topLeft" />
            <div className="fieldDropDown__trigger-triangle fieldDropDown__trigger-triangle--topRight" />
            <div className="fieldDropDown__trigger-triangle fieldDropDown__trigger-triangle--bottomRight" />
            <div className="fieldDropDown__trigger-triangle fieldDropDown__trigger-triangle--bottomLeft" />
          </>
        )}
      </div>
      {isMenuOpen && (
        <div
          ref={dropdownMenuRef}
          style={dropDownMenuStyles}
          className={classNames('fieldDropDown__content-wrapper', dropdownClassName)}
        >
          <p className="fieldDropDown__title">
            Who {fieldType === 'checkbox' ? 'check' : 'fills'} this out?
          </p>
          {renderFieldMenu?.()}
        </div>
      )}
    </div>
  );
};

export default forwardRef<HTMLDivElement, FieldItemViewProps>(FieldItemView);
